import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Loader2, Lock, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { BRAND } from '../data/mock';

const AdminLogin = () => {
  const { login, user, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (loading) return null;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate('/admin', { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : 'Login failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <div className="w-full max-w-md" data-testid="admin-login-card">
        <div className="text-center mb-6">
          <img src={BRAND.logo} alt="Mr. Coconetto" className="w-16 h-16 object-contain mx-auto rounded-full shadow-soft bg-white p-1" />
          <div className="mt-3 font-script text-pink-brand text-lg">Simply Original</div>
          <h1 className="font-display font-bold text-3xl text-stone-900">Mr. Coconetto</h1>
          <p className="mt-1 text-sm text-stone-500">Admin Console</p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-3xl shadow-floating border border-pink-100 p-8 space-y-5"
        >
          <h2 className="font-display font-bold text-2xl text-stone-900">Sign in</h2>
          <div>
            <Label htmlFor="admin-email" className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
              <Mail className="w-3 h-3 inline mr-1 text-pink-brand" /> Email
            </Label>
            <Input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@mrcoconetto.com"
              className="mt-1"
              autoFocus
              data-testid="admin-login-email-input"
            />
          </div>
          <div>
            <Label htmlFor="admin-password" className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
              <Lock className="w-3 h-3 inline mr-1 text-pink-brand" /> Password
            </Label>
            <Input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1"
              data-testid="admin-login-password-input"
            />
          </div>

          {err && (
            <div className="rounded-lg bg-red-50 text-red-700 text-sm px-4 py-2 border border-red-200" data-testid="admin-login-error">
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-pink-brand hover:bg-[#BE185D] text-white py-6 font-semibold shadow-pink-glow disabled:opacity-70"
            data-testid="admin-login-submit-button"
          >
            {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…</>) : 'Sign in'}
          </Button>
          <p className="text-center text-xs text-stone-500">
            For internal team only — enquiries dashboard access.
          </p>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
